import { useCallback, useEffect, useRef } from 'react';

type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
  removeEventListener: (type: 'release', listener: () => void) => void;
};

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};

export const useScreenWakeLock = (enabled: boolean) => {
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const releaseHandlerRef = useRef<(() => void) | null>(null);

  const releaseWakeLock = useCallback(async () => {
    const wakeLock = wakeLockRef.current;

    if (!wakeLock) return;

    if (releaseHandlerRef.current) {
      wakeLock.removeEventListener('release', releaseHandlerRef.current);
      releaseHandlerRef.current = null;
    }

    wakeLockRef.current = null;

    if (!wakeLock.released) {
      await wakeLock.release();
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    const navigatorWithWakeLock = navigator as NavigatorWithWakeLock;

    if (!navigatorWithWakeLock.wakeLock || wakeLockRef.current || document.hidden) {
      return;
    }

    try {
      const wakeLock = await navigatorWithWakeLock.wakeLock.request('screen');
      const handleRelease = () => {
        if (wakeLockRef.current === wakeLock) {
          wakeLockRef.current = null;
        }
      };

      wakeLock.addEventListener('release', handleRelease);
      releaseHandlerRef.current = handleRelease;
      wakeLockRef.current = wakeLock;
    } catch (error) {
      console.warn('AI Scribe screen wake lock request failed:', error);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [enabled, releaseWakeLock, requestWakeLock]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (enabled && document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, requestWakeLock]);

  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);
};
